import { FormServiceAddSubmissionEvent } from '../../events/FormServiceAddSubmissionEvent'
import { FormServiceUpdateSubmissionEvent } from '../../events/FormServiceUpdateSubmissionEvent'
import { FormDTO } from '../../models/FormDTO.model'
import { FormFieldDTO } from '../../models/FormFieldDTO.model'
import { FormFieldRelations } from '../../models/FormFieldRelations.model'
import { FormRelations } from '../../models/FormRelations.model'
import { FormSubmissionDTO } from '../../models/FormSubmissionDTO.model'
import ScrubstrApiQueryService, {
  ScrubstrApiQueryServiceGetEvent,
} from '../ScrubstrApiQueryService/ScrubstrApiQueryService'

export class FormService {
  /**
   * @description Fetches a form by id
   *
   * @param id
   *
   * @returns promise for a Form
   */
  public async get({ id, relations }: { id: number; relations?: FormRelations[] }): Promise<FormDTO> {
    const params = {
      relations: relations ? relations.join(',') : undefined,
    }
    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: `form/${id}`,
      params,
    }

    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const formRaw = await scrubstrApiQueryService.get<FormDTO>(scrubstrApiQueryRequest)
    const form: FormDTO = {
      createdAt: formRaw.createdAt,
      id: formRaw.id,
      name: formRaw.name,
      submitAction: formRaw.submitAction,
      updatedAt: formRaw.updatedAt,
    }
    if (formRaw.pages) {
      form.pages = formRaw.pages.map((page) => {
        if (page.fields) {
          // sort the fields by the order property and then the id property and then set the fields property on the form object
          page.fields = page.fields.sort((a, b) => {
            if (a.order === b.order) {
              return a.id - b.id
            }
            return a.order - b.order
          })
        }
        return page
      })
    }
    if (formRaw.fields) {
      // sort the fields by the order property and then the id property and then set the fields property on the form object
      form.fields = formRaw.fields.sort((a, b) => {
        if (a.order === b.order) {
          return a.id - b.id
        }
        return a.order - b.order
      })
    }
    if (formRaw.submissions) {
      form.submissions = formRaw.submissions
    }
    return form
  }

  /**
   * @description Fetches a form field by id
   *
   * @param id
   *
   * @returns promise for a Form
   */
  public async getField({ id, relations }: { id: number; relations?: FormFieldRelations[] }): Promise<FormFieldDTO> {
    const params = {
      relations: relations ? relations.join(',') : undefined,
    }
    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: `form-field/${id}`,
      params,
    }

    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const formFieldRaw = await scrubstrApiQueryService.get<FormFieldDTO>(scrubstrApiQueryRequest)
    const formField: FormFieldDTO = {
      choices: formFieldRaw.choices,
      createdAt: formFieldRaw.createdAt,
      defaultValue: formFieldRaw.defaultValue,
      id: formFieldRaw.id,
      inputName: formFieldRaw.inputName,
      isRating: formFieldRaw.isRating,
      isRequired: formFieldRaw.isRequired,
      label: formFieldRaw.label,
      order: formFieldRaw.order,
      placeholder: formFieldRaw.placeholder,
      type: formFieldRaw.type,
      updatedAt: formFieldRaw.updatedAt,
      visible: formFieldRaw.visible,
    }
    if (formFieldRaw.form) {
      formField.form = formFieldRaw.form
    }
    if (formFieldRaw.page) {
      formField.page = formFieldRaw.page
    }
    if (formFieldRaw.answers) {
      formField.answers = formFieldRaw.answers
    }
    return formField
  }

  /**
   * @description Adds a new form submission
   *
   * @param formSubmission
   *
   * @returns promise for the created form submission
   */
  public async addSubmission(formSubmission: FormServiceAddSubmissionEvent): Promise<FormSubmissionDTO> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    return await scrubstrApiQueryService.post<FormSubmissionDTO>({
      data: formSubmission,
      endpoint: 'form-submission',
    })
  }

  /**
   * @description Fetches all form submissions for the authenticated user
   *
   * @returns promise for an array of FormSubmissions
   */
  public async getMyReviews(): Promise<FormSubmissionDTO[]> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: 'form-submission/my-reviews',
    }
    return await scrubstrApiQueryService.get<FormSubmissionDTO[]>(scrubstrApiQueryRequest)
  }

  /**
   * @description Updates an existing form submission
   *
   * @param id - The submission ID
   * @param formSubmission - The update data
   *
   * @returns promise void
   */
  public async updateSubmission(id: number, formSubmission: FormServiceUpdateSubmissionEvent): Promise<void> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    await scrubstrApiQueryService.patch({
      data: formSubmission,
      endpoint: `form-submission/${id}`,
    })
  }

  /**
   * @description Deletes a form submission
   *
   * @param id - The submission ID
   *
   * @returns promise void
   */
  public async deleteSubmission(id: number): Promise<void> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    await scrubstrApiQueryService.delete({
      endpoint: `form-submission/${id}`,
    })
  }
}
